import React, { useRef, useMemo } from "react"
import { WarningOutlined } from "@ant-design/icons"
import { Typography, Space, Tooltip } from "antd"
import { useOutletContext, useParams } from "react-router-dom"

import { hasAccess } from "@components/Authorization"
import { MarkdownAlert } from "@components/Text"

import ProjectUpdateModal from "../ProjectUpdateModal"

const LABEL_CHANGE = "Change"
const LABEL_ACCOUNT = "Account"
const LABEL_INVALID = "Invalid Bank Account"
const LABEL_MISSING = "The distribution account is missing."
const LABEL_BACKFILL = "to link one. Distributions can be imported in backfill mode."
const LABEL_CLICK_HERE = "Click here"
const LABEL_NO_APPROVER = `You are not eligible to approve distributions for
this bank account. If you believe this is a mistake, please contact **[NAME]**.`


const DistributionBankAccount = () => {
  const modal = useRef({})

  const { id: projectId } = useParams()

  const {
    getProject,
    getBankAccountName,
    isInvalidBankAccount,
    getControllerNameForBankAccount,
    canApproveBankAccountTransactions,
  } = useOutletContext()

  const project = getProject(projectId)

  const distributionBankAccountId = useMemo(() =>
    project.distributionBankAccountId
  , [ project ])

  if (!project) {
    return null
  }

  const onClick = () =>
    modal.current.open([ "distributionBankAccountId" ])

  const messsage = distributionBankAccountId
    ? (
        <Space>
          <Typography.Text strong>{LABEL_ACCOUNT}:</Typography.Text>
          {
            isInvalidBankAccount(distributionBankAccountId) && (
              <Tooltip
                placement="top"
                title={LABEL_INVALID}
              >
                <WarningOutlined style={{ color: "red" }} />
              </Tooltip>
            )
          }
          {getBankAccountName(distributionBankAccountId)}
          <Typography.Link onClick={onClick}>{LABEL_CHANGE}</Typography.Link>
        </Space>
      )
    : (
        <Typography.Text>
          {LABEL_MISSING}
          {" "}
          <Typography.Link onClick={onClick}>{LABEL_CLICK_HERE}</Typography.Link>
          {" "}
          {LABEL_BACKFILL}
        </Typography.Text>
      )

  const renderNotApproverBanner = () => {
    const canApproveOrReject = hasAccess(['transactions-approve'])

    if (!canApproveOrReject) {
      return null
    }

    if (!distributionBankAccountId) {
      return null
    }

    const isApprover = canApproveBankAccountTransactions(distributionBankAccountId)

    if (isApprover) {
      return null
    }

    const controllerName = getControllerNameForBankAccount(distributionBankAccountId)
    const text = LABEL_NO_APPROVER.replace('[NAME]', controllerName)

    return (
      <MarkdownAlert
        type="warning"
        text={text}
      />
    )
  }

  return (
    <>
      <Typography.Paragraph>
        {messsage}
      </Typography.Paragraph>

      {renderNotApproverBanner()}

      <ProjectUpdateModal
        modal={modal}
        project={project}
      />
    </>
  )
}

export default DistributionBankAccount
